// Searchable country list for the skirmish setup (GDD §11): the playable roster as a radio group, filtered by name
// or capital as the player types. The country picked in the other list is excluded (a war needs two nations).

import { PLAYABLE_COUNTRIES, type CountryCode } from '@taxwar/content'
import { el } from '../../hud/dom'

const NO_MATCH_TEXT = 'No such nation on file. Try another spelling.'

interface CountryEntry {
  readonly code: CountryCode
  readonly item: HTMLLabelElement
  readonly input: HTMLInputElement
  /** Lower-cased name + capital, matched against the search text. */
  readonly haystack: string
}

export class CountryPicker {
  readonly element: HTMLFieldSetElement
  private readonly entries: readonly CountryEntry[]
  private readonly noMatch: HTMLElement
  private selected: CountryCode | null = null

  constructor(legend: string, groupName: string, onChange: () => void, signal: AbortSignal) {
    this.element = el('fieldset', 'tw-picker tw-panel')
    const search = el('input', 'tw-picker__search')
    search.type = 'search'
    search.placeholder = 'Search nations or capitals'
    search.setAttribute('aria-label', `${legend}: search`)
    search.autocomplete = 'off'
    search.spellcheck = false
    const list = el('div', 'tw-picker__list')
    list.setAttribute('role', 'radiogroup')
    list.setAttribute('aria-label', legend)
    this.entries = PLAYABLE_COUNTRIES.map((country) => {
      const input = el('input', 'tw-picker__radio')
      input.type = 'radio'
      input.name = groupName
      input.value = country.code
      input.addEventListener('change', () => {
        this.selected = country.code
        onChange()
      }, { signal })
      const item = el('label', 'tw-picker__item')
      item.append(
        input,
        el('span', 'tw-picker__name', country.name),
        el('span', 'tw-picker__meta tw-dim', `${country.capital.name} · ${country.cities.length} cities`),
      )
      list.append(item)
      return { code: country.code, item, input, haystack: `${country.name} ${country.capital.name}`.toLowerCase() }
    })
    this.noMatch = el('p', 'tw-picker__none tw-dim', NO_MATCH_TEXT)
    this.noMatch.hidden = true
    search.addEventListener('input', () => this.filter(search.value), { signal })
    this.element.append(el('legend', 'tw-heading', legend), search, list, this.noMatch)
  }

  get value(): CountryCode | null {
    return this.selected
  }

  /** Makes `code` unpickable here (it is the other side's nation), dropping it if it was picked. */
  exclude(code: CountryCode | null): void {
    for (const entry of this.entries) {
      const excluded = entry.code === code
      entry.input.disabled = excluded
      entry.item.classList.toggle('is-excluded', excluded)
      if (excluded && entry.input.checked) {
        entry.input.checked = false
        this.selected = null
      }
    }
  }

  private filter(text: string): void {
    const query = text.trim().toLowerCase()
    let visible = 0
    for (const entry of this.entries) {
      const match = entry.haystack.includes(query)
      entry.item.hidden = !match
      if (match) visible++
    }
    this.noMatch.hidden = visible > 0
  }
}
